<template>
  <div class="app-shell">
    <PageLoader :visible="isInitialLoading" />

    <div
      class="site-content"
      :class="{ 'site-content--hidden': isInitialLoading }"
      :aria-hidden="isInitialLoading"
      >
      <NavBarComponent />

      <main class="page-shell">
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <div class="page-wrapper" v-if="Component">
              <component :is="Component" :key="$route.fullPath" />
            </div>
          </Transition>
        </RouterView>
      </main>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import NavBarComponent from './components/NavBarComponent.vue'
import PageLoader from './components/PageLoader.vue'

const router = useRouter()
const route = useRoute()

// Track initialization state - starts false, becomes true after first load
let isInitialized = false
const isInitialLoading = ref(true)
let loaderTimeout

const stopLoading = (delay = 0) => {
  window.clearTimeout(loaderTimeout)
  loaderTimeout = window.setTimeout(() => {
    isInitialLoading.value = false
    isInitialized = true
  }, delay)
}

onMounted(() => {
  // If already initialized (navigating between routes), don't show loader
  if (isInitialized) {
    isInitialLoading.value = false
    return
  }

  const splashMinimum = 3000
  const start = performance.now()

  // Wait for router to be ready and the first route to be fully resolved
  Promise.all([
    router.isReady(),
    new Promise(resolve => {
      // If route component is already available, resolve immediately
      if (route.matched.length > 0) {
        resolve()
      } else {
        // Otherwise wait for the first route to be matched
        const unwatch = watch(
          () => route.matched.length,
          (newVal) => {
            if (newVal > 0) {
              unwatch()
              resolve()
            }
          },
          { immediate: true }
        )
      }
    })
  ]).then(() => {
    const elapsed = performance.now() - start
    stopLoading(Math.max(0, splashMinimum - elapsed))
  })
})

onUnmounted(() => {
  window.clearTimeout(loaderTimeout)
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Alumni+Sans+Pinstripe:ital@0;1&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  width: 100%;
  height: 100%;
  overflow: auto;
  background: black;
}

#app {
  font-family: "Courier New";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-shell {
  min-height: 100vh;
  width: 100vw;
  background: black;
  color: white;
  position: relative;
  overflow-x: hidden;
}

.site-content {
  transition: opacity 0.4s ease, visibility 0.4s ease;
}

.site-content--hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.page-shell {
  min-height: 100vh;
  width: 100%;
}

.page-wrapper {
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  background: black;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(50px);
}

.page-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.page-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style>